import { getDB } from "./schema.js";
import { nowIso } from "../utils/dates.js";
import { listMovies } from "./movies.js";
import { listTvSeries } from "./tv.js";
import { listBooks } from "./books.js";
import { listGames } from "./games.js";
import { listAnime } from "./anime.js";
import { setPersonFacets } from "../utils/filters.js";
import { followsChanged } from "./cloud-follows.js";

// Followed people for every category (design_documents/people.md) — the
// `followed_people` store (DB v21; device-local for now, backed up). Comic
// creators (source "metron") share it and keep their own helpers in
// db/comic-creators.js. A person is "<source>:<person id>".

const STORE = "followed_people";

// Which category each source's people belong to (Phase 1; games/anime later).
export const SOURCE_MEDIA = {
	wikidata: "movie",
	tvmaze: "tv",
	openlibrary: "book",
	metron: "comic",
	igdb: "videogame",
};

export function personKey(source, personId) {
	return `${source}:${personId}`;
}

export async function listFollowedPeople() {
	const db = await getDB();
	const all = await db.getAll(STORE);
	return all.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
}

export async function getFollowedPerson(source, personId) {
	const db = await getDB();
	return db.get(STORE, personKey(source, personId));
}

export async function followPerson({ source, person_id, name, image = null, media_type = null, seen_work_ids = null }) {
	const db = await getDB();
	const key = personKey(source, person_id);
	const existing = await db.get(STORE, key);
	const record = {
		person_key: key,
		source,
		external_id: String(person_id),
		media_type: media_type ?? SOURCE_MEDIA[source] ?? null,
		name,
		image,
		role_group: existing?.role_group ?? "all",
		// Prepared for a later cross-media merge (decision 5).
		wikidata_id: source === "wikidata" ? String(person_id) : (existing?.wikidata_id ?? null),
		added_at: existing?.added_at ?? nowIso(),
		// "New from …" (db/people-watch.js): what we've already seen of theirs.
		seen_work_ids: seen_work_ids ?? existing?.seen_work_ids ?? [],
		last_works_check: seen_work_ids ? new Date().toISOString().slice(0, 10) : (existing?.last_works_check ?? null),
	};
	await db.put(STORE, record);
	await loadPersonFacets();
	followsChanged();
	return record;
}

// Refresh the list filters' "People you follow" options (utils/filters.js).
export async function loadPersonFacets() {
	const db = await getDB();
	setPersonFacets(await db.getAll(STORE));
}

export async function unfollowPerson(source, personId) {
	const db = await getDB();
	await db.delete(STORE, personKey(source, personId));
	await loadPersonFacets();
	followsChanged();
}

// Library lookup for a person's works: external id → item id, per category.
export async function libraryIndex(mediaType) {
	const map = new Map();
	if (mediaType === "movie") for (const m of await listMovies()) map.set(String(m.imdb_id), m.id);
	if (mediaType === "tv") for (const t of await listTvSeries()) map.set(String(t.tvmaze_id), t.id);
	if (mediaType === "book") for (const b of await listBooks()) map.set(String(b.ol_id), b.id);
	if (mediaType === "videogame") for (const g of await listGames()) map.set(String(g.igdb_id), g.id);
	if (mediaType === "anime") for (const a of await listAnime()) if (a.mal_id) map.set(String(a.mal_id), a.id);
	return map;
}
